/**
 * Chip Collect Effect for Poker Reborn Effects Engine
 *
 * Animates chips being collected to the winner.
 *
 * @see tasks/active/EFFECTS_ENGINE_IMPLEMENTATION_PLAN.md - P4.2.4
 */

import { Container, Graphics, Sprite } from 'pixi.js';
import { TimedEffect } from '../../core/TimedEffect';
import type { EffectTarget } from '../../core/EffectTypes';
import { EffectPriority } from '../../core/EffectTypes';
import type { EffectOptions } from '../../core/Effect';
import { easeInOutCubic } from '../../utils/easing';
import {
  getChipDenominationsForAmount,
  createChipSprite,
  CHIP_FLYING_SCALE,
  getChipFlyingScale,
} from '../../../components/chips/ChipColors';
import { debugOverlay } from '../../../utils/DebugOverlay';

// =============================================================================
// TYPES
// =============================================================================

export interface ChipCollectData {
  /** The container to add chips to */
  container: Container;
  /** Starting X (pot position) */
  fromX: number;
  /** Starting Y (pot position) */
  fromY: number;
  /** Ending X (winner position) */
  toX: number;
  /** Ending Y (winner position) */
  toY: number;
  /** Amount being collected */
  amount: number;
  /** Number of chip sprites to animate */
  chipCount?: number | undefined;
  /** Stagger delay between chips */
  stagger?: number | undefined;
  /** Chip colors */
  colors?: number[] | undefined;
  /** Callback when animation completes */
  onComplete?: (() => void) | undefined;
  /** Current screen width for BBS scaling */
  screenWidth?: number | undefined;
}

// =============================================================================
// DEFAULTS
// =============================================================================

const DEFAULT_DURATION = 500; // ms
const DEFAULT_CHIP_COUNT = 8;
const DEFAULT_STAGGER = 30; // ms
const DEFAULT_COLORS = [0xe53935, 0x43a047, 0x1e88e5, 0xfdd835];

// =============================================================================
// COLLECTED CHIP
// =============================================================================

interface CollectedChip {
  displayObject: Sprite | Graphics;
  startX: number;
  startY: number;
  offsetAngle: number;
}

// =============================================================================
// CHIP COLLECT EFFECT (P4.2.4)
// =============================================================================

/**
 * Animates chips flying from pot to winner.
 */
export class ChipCollectEffect extends TimedEffect {
  private readonly collectData: ChipCollectData;
  private chips: CollectedChip[] = [];
  private readonly flyingScale: number;

  constructor(
    target: EffectTarget,
    data: ChipCollectData,
    options: EffectOptions = {}
  ) {
    // Capture callbacks to merge with our cleanup
    const userCallbacks = options.callbacks ?? {};
    const userOnComplete = userCallbacks.onComplete;

    super('ChipCollect', target, options.duration ?? DEFAULT_DURATION, {
      ...options,
      priority: options.priority ?? EffectPriority.HIGH,
      callbacks: {
        ...userCallbacks,
        onComplete: () => {
          debugOverlay.log(`💰 PotAward DONE: $${data.amount}`);
          // Cleanup flying chips
          this.cleanup();
          // Call data callback
          data.onComplete?.();
          // Call user callback
          userOnComplete?.();
        },
      },
    });

    this.collectData = data;
    this.flyingScale = data.screenWidth ? getChipFlyingScale(data.screenWidth) : CHIP_FLYING_SCALE;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  protected override onPlay(): void {
    const { container, fromX, fromY, toX, toY, chipCount, colors, amount } = this.collectData;

    const targetInfo = this.target.type === 'seat' ? `seat ${this.target.id}` : this.target.type;
    debugOverlay.log(`💰 PotAward START: $${amount} → ${targetInfo}`);
    debugOverlay.log(`   from: (${Math.round(fromX)}, ${Math.round(fromY)}) → to: (${Math.round(toX)}, ${Math.round(toY)})`);

    const count = chipCount ?? DEFAULT_CHIP_COUNT;
    const chipColors = colors ?? DEFAULT_COLORS;

    // Get denominations for sprites
    const denominations = getChipDenominationsForAmount(amount, count);

    // Create chips starting at pot with slight spread
    this.chips = [];

    for (let i = 0; i < count; i++) {
      const denomination = denominations[i % denominations.length] ?? 1;

      // Try to create sprite, fall back to graphics
      let displayObject: Sprite | Graphics;
      const sprite = createChipSprite(denomination);
      if (sprite) {
        displayObject = sprite;
      } else {
        // Fallback to graphics
        const graphics = new Graphics();
        const chipRadius = 10 + Math.random() * 3;
        const color = chipColors[i % chipColors.length]!;
        graphics.circle(0, 0, chipRadius);
        graphics.fill(color);
        graphics.circle(-2, -2, chipRadius * 0.2);
        graphics.fill(0xffffff);
        displayObject = graphics;
      }

      // Slight offset from center
      const offsetAngle = (Math.PI * 2 * i) / count;
      const offsetRadius = 10 + Math.random() * 10;
      const startX = fromX + Math.cos(offsetAngle) * offsetRadius;
      const startY = fromY + Math.sin(offsetAngle) * offsetRadius;

      displayObject.x = startX;
      displayObject.y = startY;
      displayObject.scale.set(0.8 * this.flyingScale);

      container.addChild(displayObject);

      this.chips.push({
        displayObject,
        startX,
        startY,
        offsetAngle,
      });
    }

    super.onPlay();
  }

  protected onTick(progress: number): void {
    const { toX, toY, stagger } = this.collectData;
    const staggerTime = stagger ?? DEFAULT_STAGGER;

    this.chips.forEach((chip, index) => {
      // Calculate staggered progress for this chip
      const staggerOffset = (index * staggerTime) / this.duration;
      const chipProgress = Math.max(
        0,
        Math.min(1, (progress - staggerOffset) / (1 - staggerOffset))
      );

      if (chipProgress <= 0) return;

      // Apply easing
      const eased = easeInOutCubic(chipProgress);

      // Interpolate position
      chip.displayObject.x = chip.startX + (toX - chip.startX) * eased;
      chip.displayObject.y = chip.startY + (toY - chip.startY) * eased;

      // Add arc for natural movement
      const arcHeight = 30;
      const arcOffset = -arcHeight * 4 * chipProgress * (1 - chipProgress);
      chip.displayObject.y += arcOffset;

      // Scale: pop in then normal
      if (chipProgress < 0.2) {
        chip.displayObject.scale.set((0.8 + chipProgress * 1.5) * this.flyingScale);
      } else {
        chip.displayObject.scale.set((1.1 - (chipProgress - 0.2) * 0.125) * this.flyingScale);
      }

      // Fade out near end
      if (chipProgress > 0.8) {
        chip.displayObject.alpha = 1 - (chipProgress - 0.8) * 5;
      } else {
        chip.displayObject.alpha = 1;
      }
    });
  }

  protected override onCancel(): void {
    super.onCancel();
    this.cleanup();
  }

  private cleanup(): void {
    const { container } = this.collectData;

    this.chips.forEach((chip) => {
      container.removeChild(chip.displayObject);
      chip.displayObject.destroy();
    });

    this.chips = [];
  }
}

// =============================================================================
// FACTORY FUNCTION
// =============================================================================

/**
 * Create a ChipCollectEffect from cue data.
 */
export function createChipCollectEffect(
  target: EffectTarget,
  data: Record<string, unknown>,
  options: EffectOptions = {}
): ChipCollectEffect | null {
  const container = data['container'];
  if (!(container instanceof Container)) {
    return null;
  }

  const collectData: ChipCollectData = {
    container,
    fromX: (data['fromX'] as number) ?? 0,
    fromY: (data['fromY'] as number) ?? 0,
    toX: (data['toX'] as number) ?? 0,
    toY: (data['toY'] as number) ?? 0,
    amount: (data['amount'] as number) ?? 0,
    chipCount: data['chipCount'] as number | undefined,
    stagger: data['stagger'] as number | undefined,
    colors: data['colors'] as number[] | undefined,
    onComplete: data['onComplete'] as (() => void) | undefined,
    screenWidth: data['screenWidth'] as number | undefined,
  };

  return new ChipCollectEffect(target, collectData, options);
}
